import React, { useEffect, ReactNode } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { SpinLoading } from 'antd-mobile';
import { isMobile } from 'react-device-detect';
import { useSnapshot } from 'valtio';
import { appStateProxy } from '@/hooks/useAppState.ts';
import { usePageThemeColor } from '@/hooks/usePageThemeColor.ts';
import OnboardingWizard from '@components/OnboardingWizard/OnboardingWizard.tsx';
import OnboardingWizardMobile from '@components/OnboardingWizard/OnboardingWizardMobile.tsx';
import { UserRole } from '@spona/rest-api-types';

interface AuthenticatedLayoutProps {
    children: ReactNode;
}

/**
 * Layout component that ensures user is authenticated
 * Redirects to /login if not authenticated after initialization completes
 */
const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({ children }) => {
    const history = useHistory();
    const appStateSnap = useSnapshot(appStateProxy);
    usePageThemeColor('spona-bg-container');
    
    useEffect(() => {
        if (appStateSnap.isInitialized && !appStateSnap.user) {
            history.replace('/login');
        }
    }, [appStateSnap.isInitialized, appStateSnap.user, history]);
    
    // Show loading spinner while initializing
    if (!appStateSnap.isInitialized) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                width: '100vw'
            }}>
                <SpinLoading style={{ '--size': '32px' }} color="var(--spona-brand-primary)" />
            </div>
        );
    }
    
    // Redirect if not authenticated
    if (!appStateSnap.user) {
        return <Redirect to="/login" />;
    }

    // Show onboarding wizard for non-superadmin users who haven't completed onboarding
    const needsOnboarding =
        appStateSnap.user.onboardingCompleted === false &&
        appStateSnap.user.role !== UserRole.SUPERADMIN;

    return (
        <>
            {children}
            {needsOnboarding && (isMobile ? <OnboardingWizardMobile /> : <OnboardingWizard />)}
        </>
    );
};

export default AuthenticatedLayout;
